"use client";

import { useEffect, useRef } from "react";
import { setSignupAttribution } from "@/lib/api/generated";
import {
  clearStoredAttribution,
  getStoredAttribution,
} from "@/lib/attribution";
import { getStoredCometToken } from "@/lib/cometly";

/** Backend messages that mean the client snapshot can be discarded. */
const CLEAR_CLIENT_MESSAGES = new Set([
  "Attribution recorded",
  "Attribution already set",
]);

/**
 * One-shot sender for first-touch signup attribution.
 *
 * Mounts inside the authenticated dashboard layout, so by the time it runs
 * a session exists — this is the single convergence point for every signup
 * path (email+password, OAuth, invites). The backend endpoint is
 * write-once and ignores stale accounts. We only clear localStorage when the
 * backend confirms a write or that attribution is already on the user —
 * stale-account no-ops leave the snapshot so a later signup on this browser
 * can still claim first-touch.
 */
export function SignupAttributionSync() {
  const sentRef = useRef(false);

  useEffect(() => {
    if (sentRef.current) return;
    const attribution = getStoredAttribution();
    // The Cometly click token rides along (last-write-wins on the backend);
    // it is kept in localStorage afterwards — checkout reuses it and a newer
    // ad click must still be able to refresh it later.
    const cometToken = getStoredCometToken();
    const hasAttribution =
      attribution !== null && Object.keys(attribution).length > 0;
    if (!hasAttribution && !cometToken) return;
    sentRef.current = true;

    setSignupAttribution({ ...(attribution ?? {}), comet_token: cometToken })
      .then((res) => {
        if (CLEAR_CLIENT_MESSAGES.has(res.message)) {
          clearStoredAttribution();
          return;
        }
        // Stale / empty no-op: keep the snapshot for a later signup on this
        // browser, but do not retry on every layout remount this session.
      })
      .catch(() => {
        // Best-effort: leave the snapshot in place so a later app load
        // retries. The endpoint is idempotent.
        sentRef.current = false;
      });
  }, []);

  return null;
}
